/**
 * Sentry browser wrapper for NIZAM ONE.
 *
 * Init is GATED on VITE_SENTRY_DSN — when empty (default), every function in
 * this module is a no-op so dev/CI/preview builds carry zero Sentry overhead.
 *
 * Saudi PII rules: scrub IBAN, CR (Commercial Registration), VAT number, and
 * employee national ID (IQAMA) before any event leaves the browser.
 *
 * Coexists with the in-house errorTracker (writes to public.system_error_logs):
 * both sinks fire in parallel via lib/monitoring/errorTracker.ts.
 */

import * as Sentry from '@sentry/react';

let initialized = false;

const SENTRY_DSN     = import.meta.env.VITE_SENTRY_DSN     as string | undefined;
const SENTRY_ENV     = import.meta.env.VITE_SENTRY_ENV     as string | undefined;
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
const SENTRY_DIST    = import.meta.env.VITE_SENTRY_DIST    as string | undefined;

/** Saudi-PII redaction. Matches IBAN (SA+22 digits), VAT (15 digits), CR (10 digits), IQAMA (10 digits). */
const PII_PATTERNS: Array<{ re: RegExp; replacement: string }> = [
  { re: /\bSA\d{22}\b/g,                replacement: '[REDACTED_IBAN]' },
  { re: /\b3\d{14}\b/g,                 replacement: '[REDACTED_VAT]' },   // KSA VAT starts with 3, 15 digits
  { re: /\b[12]\d{9}\b/g,               replacement: '[REDACTED_ID]' },    // IQAMA / National ID, starts 1 or 2
];

function scrubString(input: string): string {
  let out = input;
  for (const { re, replacement } of PII_PATTERNS) out = out.replace(re, replacement);
  return out;
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  // Scrub message + exception values + breadcrumbs + request data
  if (event.message) event.message = scrubString(event.message);
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value);
    }
  }
  if (event.breadcrumbs) {
    for (const bc of event.breadcrumbs) {
      if (bc.message)             bc.message             = scrubString(bc.message);
      if (typeof bc.data === 'object' && bc.data) {
        for (const k of Object.keys(bc.data)) {
          const v = bc.data[k];
          if (typeof v === 'string') bc.data[k] = scrubString(v);
        }
      }
    }
  }
  return event;
}

/**
 * Call once from src/main.tsx BEFORE React renders.
 * No-op when VITE_SENTRY_DSN is not set.
 */
export function initSentry(): void {
  if (initialized) return;
  if (!SENTRY_DSN)  return;     // Disabled in dev / when DSN absent
  initialized = true;

  Sentry.init({
    dsn:                  SENTRY_DSN,
    environment:          SENTRY_ENV     ?? import.meta.env.MODE,
    release:              SENTRY_RELEASE ?? undefined,
    // dist disambiguates artifacts when the same release ships multiple builds
    // (e.g. different platforms or rebuilds). Aligns source-map uploads to the
    // exact bundle the browser is executing.
    dist:                 SENTRY_DIST    ?? undefined,
    // Performance: sample 10% of traces. Profiling stays off (per plan).
    tracesSampleRate:     0.1,
    // Replays off by default (privacy, bandwidth, KSA data-residency caution)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Don't auto-send PII headers
    sendDefaultPii:       false,
    beforeSend:           scrubEvent,
  });
}

/** Capture an exception. No-op if Sentry is not initialized. */
export function captureException(err: unknown, ctx?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(err, { extra: ctx });
}

/** Capture a message at the given level. No-op if Sentry is not initialized. */
export function captureMessage(
  msg: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  ctx?: Record<string, unknown>,
): void {
  if (!initialized) return;
  Sentry.captureMessage(scrubString(msg), { level, extra: ctx });
}

/** Tag the current scope with user / tenant context. Safe when not initialized. */
export function setUserContext(user: {
  id?:         string | null;
  role?:       string | null;
  company_id?: string | null;
} | null): void {
  if (!initialized) return;
  if (!user) { Sentry.setUser(null); return; }
  Sentry.setUser({ id: user.id ?? undefined });
  Sentry.setTag('role',       user.role       ?? 'unknown');
  Sentry.setTag('company_id', user.company_id ?? 'none');
}

/** Re-export ErrorBoundary so consumers don't need a direct @sentry/react import. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;
